import { ArrowLeft, Battery, BatteryCharging, FlaskConical, Scale, Zap } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ELECTROCHEM_MODES,
  ELECTROCHEM_TOOL_IDENTITY,
  ELECTROCHEM_YEAR_MODES,
  type ElectrochemMode,
} from './electrochemicalCells';
import { BatteriesMode } from './ElectrochemicalBatteriesMode';
import { HalfEquationMode } from './ElectrochemicalHalfMode';
import { ElectrolysisMode } from './ElectrochemicalElectrolysisMode';
import { GalvanicCellMode } from './ElectrochemicalGalvanicMode';
import { StudyShell } from './StudyShell';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<
  ElectrochemMode,
  { tab: string; eyebrow: string; title: string; description: string }
> = {
  half: {
    tab: 'Half-equations',
    eyebrow: 'Electron bookkeeping',
    title: 'Balance a half-equation',
    description:
      'Decide whether electrons are lost or gained, then balance atoms and charge, including in acidic solution.',
  },
  galvanic: {
    tab: 'Galvanic cells',
    eyebrow: 'Chemical energy to electrical energy',
    title: 'Pair two half-cells',
    description:
      'Predict the cathode from standard reduction potentials, then see electron flow, salt-bridge ions and E°cell.',
  },
  electrolysis: {
    tab: 'Electrolysis',
    eyebrow: 'Electrical energy drives a reaction',
    title: 'Predict the electrode products',
    description:
      'Choose a molten or reviewed aqueous electrolyte, predict each product, then use Faraday’s law.',
  },
  batteries: {
    tab: 'Batteries',
    eyebrow: 'Cells in everyday devices',
    title: 'Run a battery or fuel cell',
    description:
      'Predict the anode while the cell discharges or recharges and whether it is primary, secondary or a fuel cell.',
  },
};

const MODE_ICONS: Record<ElectrochemMode, typeof Scale> = {
  half: Scale,
  galvanic: Battery,
  electrolysis: FlaskConical,
  batteries: BatteryCharging,
};

export function ElectrochemicalCellsLabPage() {
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<ElectrochemMode>(() =>
    modeForYear(ELECTROCHEM_YEAR_MODES, year),
  );
  const copy = MODE_COPY[mode];
  const ModeIcon = MODE_ICONS[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'electrochemical-cells', state: { mode } }}>
      <Link
        to="/learn/study/subjects/chemistry/tools"
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-sunshine/40 bg-gradient-to-br from-wash-sunshine via-canvas-pure to-wash-sky p-6 shadow-card-soft lg:p-8"
        data-tool-key={ELECTROCHEM_TOOL_IDENTITY.toolKey}
        data-tool-version={ELECTROCHEM_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-coral">
              <Zap className="h-4 w-4" aria-hidden="true" /> Redox and electrochemistry
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Electrochemical Cells Lab
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Follow electrons from balanced half-equations to galvanic cells, electrolysis and everyday batteries.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            {year === null ? 'Year 11–12 · senior chemistry' : `Year ${year} view`}
          </div>
        </div>

        <div
          className="mt-6 grid max-w-3xl grid-cols-2 gap-1 rounded-3xl bg-white/75 p-1 sm:grid-cols-4 sm:rounded-full"
          aria-label="Choose a mode"
        >
          {ELECTROCHEM_MODES.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => setMode(option)}
              className={`rounded-full px-2 py-2.5 text-xs font-extrabold transition-colors motion-reduce:transition-none sm:px-3 sm:text-sm ${
                mode === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {MODE_COPY[option].tab}
            </button>
          ))}
        </div>
      </header>

      <section className="my-6 rounded-[1.75rem] border border-hairline bg-canvas-pure px-5 py-4 shadow-card-soft sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
              {copy.eyebrow}
            </p>
            <h2 className="mt-1 text-xl font-extrabold text-ink">{copy.title}</h2>
            <p className="mt-1 text-sm font-medium leading-6 text-slate2">{copy.description}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2 rounded-full bg-wash-sunshine px-4 py-2 text-xs font-extrabold text-ink-soft">
            <ModeIcon className="h-4 w-4" aria-hidden="true" />
            Explore only · not mastery evidence
          </div>
        </div>
      </section>

      {mode === 'half' ? <HalfEquationMode /> : null}
      {mode === 'galvanic' ? <GalvanicCellMode /> : null}
      {mode === 'electrolysis' ? <ElectrolysisMode /> : null}
      {mode === 'batteries' ? <BatteriesMode /> : null}

      <footer className="my-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        This tool uses reviewed, bounded teaching cases (pending teaching review). It does not save
        attempts, replace a supervised practical, determine laboratory safety or claim alignment to
        a state syllabus.
      </footer>
    </StudyShell>
  );
}
